import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSquadDetails } from '../hooks/useSquadDetails';
import { useRealtimeSubscription } from '../hooks/useRealtimeSubscription';
import { useSquadChat } from '../hooks/useSquadChat';
import { useMeetupInfo } from '../hooks/useMeetupInfo';
import { useOrganiserLinks } from '../hooks/useOrganiserLinks';
import { useSquadMembership } from '../hooks/useSquadMembership';
import { useSquadActions } from '../hooks/useSquadActions';
import { trackPageView, trackEvent } from '../utils/analytics';
import { LoadingState } from './SquadComponents/LoadingState';
import { NotFoundState } from './SquadComponents/NotFoundState';
import { SquadHeader } from './SquadComponents/SquadHeader';
import { FullBanner } from './SquadComponents/FullBanner';
import { ProgressBar } from './SquadComponents/ProgressBar';
import { MeetupInfoCard } from './SquadComponents/MeetupInfoCard';
import { ChatAndMembers } from './SquadComponents/ChatAndMembers';
import { JoinForm } from './SquadComponents/JoinForm';
import { NewSquadButton } from './SquadComponents/NewSquadButton';
import { OrganiserLinksModal } from './SquadComponents/OrganiserLinksModal';
import Navigation from './Navigation';
import Footer from './Footer';

export default function SquadPage() {
  const params = useParams();
  const eventId = params.eventId;
  const squadId = params.squadId;

  // Auth hook
  const { attendeeUuid, authToken } = useAuth();

  // Squad details hook
  const { squadDetails, isLoading, refreshSquadDetails, setSquadDetails } = useSquadDetails(squadId);

  // Squad membership hook
  const { isUserInSquad, isHost } = useSquadMembership(squadDetails, attendeeUuid);

  // Chat hook
  const {
    messages,
    newMessage,
    setNewMessage,
    isSendingMessage,
    sendMessage,
    addMessage,
    refreshMessages,
  } = useSquadChat(squadId);

  // Meetup info hook
  const {
    meetupPoint,
    setMeetupPoint,
    meetupTime,
    setMeetupTime,
    notes,
    setNotes,
    isSaving,
    saveMessage,
    saveMeetupInfo,
  } = useMeetupInfo(isHost, squadDetails);

  // Organiser links hook
  const {
    showOrganiserModal,
    setShowOrganiserModal,
    organiserLinks,
  } = useOrganiserLinks(squadId);

  // Squad actions hook
  const {
    attendeeName,
    setAttendeeName,
    introText,
    setIntroText,
    firstTimeVenue,
    setFirstTimeVenue,
    isJoining,
    isCreatingNew,
    handleJoin,
    handleCreateNewSquad,
  } = useSquadActions(eventId, squadId, { onJoinSuccess: refreshMessages });

  // Realtime subscription handlers
  const handleSquadUpdate = useCallback((data) => {
    setSquadDetails(data);
  }, [setSquadDetails]);

  const handleMessageReceived = useCallback((message) => {
    addMessage(message);
  }, [addMessage]);

  // Set up realtime subscription
  useRealtimeSubscription(squadId, handleSquadUpdate, handleMessageReceived);

  // Handlers
  const handleJoinClick = useCallback(() => {
    handleJoin(squadDetails, attendeeUuid, authToken, refreshSquadDetails);
  }, [handleJoin, squadDetails, attendeeUuid, authToken, refreshSquadDetails]);

  const handleSaveMeetup = useCallback(() => {
    saveMeetupInfo(squadId, attendeeUuid, refreshSquadDetails);
  }, [saveMeetupInfo, squadId, attendeeUuid, refreshSquadDetails]);

  const handleSendMessageClick = useCallback(() => {
    sendMessage(attendeeUuid);
  }, [sendMessage, attendeeUuid]);

  const handleCreateNewClick = useCallback(() => {
    handleCreateNewSquad(squadDetails);
  }, [handleCreateNewSquad, squadDetails]);

  // Extract venue name from event name
  const venueName = squadDetails?.event_name?.includes('@')
    ? squadDetails.event_name.split('@')[1].trim()
    : squadDetails?.event_name || 'this venue';

  // Computed values
  const isFull = squadDetails?.squad_status === 'closed' || (squadDetails?.member_count ?? 0) >= 5;

  // Track page view and squad loaded
  useEffect(() => {
    if (!isLoading && squadDetails) {
      trackPageView('Squad Page', {
        event_id: eventId,
        squad_id: squadId,
        event_name: squadDetails.event_name,
        squad_number: squadDetails.squad_number,
        member_count: squadDetails.member_count,
        squad_status: squadDetails.squad_status,
        is_full: isFull,
        is_user_in_squad: isUserInSquad,
        is_host: isHost,
      });

      trackEvent('Squad Loaded', {
        event_id: eventId,
        squad_id: squadId,
        event_name: squadDetails.event_name,
        squad_number: squadDetails.squad_number,
        member_count: squadDetails.member_count,
        squad_status: squadDetails.squad_status,
        is_full: isFull,
        has_meetup_point: !!squadDetails.meetup_point,
        has_meetup_time: !!squadDetails.meetup_time,
        has_notes: !!squadDetails.notes,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, squadDetails, eventId, squadId, isFull, isUserInSquad, isHost]); // trackPageView and trackEvent are stable functions

  // Track when squad becomes full
  useEffect(() => {
    if (squadDetails && isFull) {
      trackEvent('Squad Full', {
        event_id: eventId,
        squad_id: squadId,
        event_name: squadDetails.event_name,
        squad_number: squadDetails.squad_number,
        member_count: squadDetails.member_count,
      });
    }
  }, [squadDetails, isFull, eventId, squadId]);

  // Track organiser modal shown
  useEffect(() => {
    if (showOrganiserModal && organiserLinks) {
      trackEvent('Organiser Links Modal Shown', {
        squad_id: squadId,
        event_id: eventId,
      });
    }
  }, [showOrganiserModal, organiserLinks, squadId, eventId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] font-inter">
        <Navigation />
        <LoadingState />
        <Footer />
      </div>
    );
  }

  if (!squadDetails) {
    return (
      <div className="min-h-screen bg-[#050505] font-inter">
        <Navigation />
        <NotFoundState />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] font-inter">
      <Navigation />
      <div className="flex min-h-[calc(100vh-200px)] items-center justify-center px-4 py-16">
        <main className="w-full max-w-6xl rounded-lg bg-gray-900 p-8 shadow-lg border border-gray-800">
          <SquadHeader
            eventName={squadDetails.event_name}
            squadNumber={squadDetails.squad_number}
          />

          {isFull && <FullBanner />}

          <ProgressBar memberCount={squadDetails.member_count} />

          <MeetupInfoCard
            squadDetails={squadDetails}
            isHost={isHost}
            meetupPoint={meetupPoint}
            setMeetupPoint={setMeetupPoint}
            meetupTime={meetupTime}
            setMeetupTime={setMeetupTime}
            notes={notes}
            setNotes={setNotes}
            isSaving={isSaving}
            saveMessage={saveMessage}
            onSave={handleSaveMeetup}
          />

          {isUserInSquad && (
            <ChatAndMembers
              messages={messages}
              members={squadDetails.members}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              isSendingMessage={isSendingMessage}
              attendeeUuid={attendeeUuid}
              onSendMessage={handleSendMessageClick}
            />
          )}

          {!isFull && !isUserInSquad && (
            <JoinForm
              attendeeName={attendeeName}
              setAttendeeName={setAttendeeName}
              introText={introText}
              setIntroText={setIntroText}
              firstTimeVenue={firstTimeVenue}
              setFirstTimeVenue={setFirstTimeVenue}
              venueName={venueName}
              isJoining={isJoining}
              onJoin={handleJoinClick}
            />
          )}

          {isFull && (
            <NewSquadButton
              isCreatingNew={isCreatingNew}
              onCreateNew={handleCreateNewClick}
            />
          )}
        </main>
      </div>

      {showOrganiserModal && organiserLinks && (
        <OrganiserLinksModal
          organiserLinks={organiserLinks}
          onClose={() => setShowOrganiserModal(false)}
        />
      )}

      <Footer />
    </div>
  );
}
